import {
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { REPORT_FLOW_COVER_SECTION_ID } from './convert-grid-report-to-flow';
import type { ReportFlowDocument } from './report-flow-types';

const SCROLL_SPY_OFFSET_PX = 140;
const REPORT_FLOW_BLOCK_ATTR = 'data-flow-block-id';

export const useReportFlowScrollSpy = (
  doc: ReportFlowDocument,
  scrollContainerRef: RefObject<HTMLElement | null>
) => {
  const [activeSectionId, setActiveSectionId] = useState<string | undefined>(
    undefined
  );

  const updateActiveSection = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const containerTop = container.getBoundingClientRect().top;
    let active = REPORT_FLOW_COVER_SECTION_ID;
    for (const section of doc.sections) {
      const element = document.getElementById(section.anchorId);
      if (
        element &&
        element.getBoundingClientRect().top - containerTop <=
          SCROLL_SPY_OFFSET_PX
      ) {
        active = section.id;
      }
    }
    setActiveSectionId(active);
  }, [doc.sections, scrollContainerRef]);

  const hasCoverEntry = doc.toc.some(
    (entry) => entry.sectionId === REPORT_FLOW_COVER_SECTION_ID
  );
  const isKnownSection = (id: string | undefined) =>
    (hasCoverEntry && id === REPORT_FLOW_COVER_SECTION_ID) ||
    doc.sections.some((section) => section.id === id);
  const defaultSectionId = hasCoverEntry
    ? REPORT_FLOW_COVER_SECTION_ID
    : doc.sections[0]?.id;

  return {
    activeSectionId: isKnownSection(activeSectionId)
      ? activeSectionId
      : defaultSectionId,
    updateActiveSection,
  };
};

type ScrollAnchor = { blockId: string; offset: number };

export const useReportFlowScrollAnchor = (
  scrollContainerRef: RefObject<HTMLElement | null>,
  restoreKey: unknown
) => {
  const anchorRef = useRef<ScrollAnchor | null>(null);

  const captureAnchor = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    if (container.scrollTop === 0) {
      anchorRef.current = null;
      return;
    }
    const containerTop = container.getBoundingClientRect().top;
    for (const element of Array.from(
      container.querySelectorAll<HTMLElement>(`[${REPORT_FLOW_BLOCK_ATTR}]`)
    )) {
      const rect = element.getBoundingClientRect();
      const blockId = element.getAttribute(REPORT_FLOW_BLOCK_ATTR);
      if (blockId && rect.bottom > containerTop) {
        anchorRef.current = { blockId, offset: rect.top - containerTop };
        return;
      }
    }
    anchorRef.current = null;
  }, [scrollContainerRef]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: restores only when the mode flips
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    const anchor = anchorRef.current;
    if (!container || !anchor) return;
    const element = container.querySelector<HTMLElement>(
      `[${REPORT_FLOW_BLOCK_ATTR}="${CSS.escape(anchor.blockId)}"]`
    );
    if (!element) return;
    const drift =
      element.getBoundingClientRect().top -
      container.getBoundingClientRect().top -
      anchor.offset;
    container.scrollTop += drift;
  }, [restoreKey]);

  return captureAnchor;
};
